import React, { useState, useRef, useEffect } from "react";
import "../../css/AttendanceView.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import api from "../../api/api";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

if (typeof jsPDF.API.autoTable === "undefined") {
  console.error("jsPDF autoTable plugin not loaded correctly.");
}

const generateDateHeadersInRange = (start, end) => {
  const startDateObj = new Date(start);
  const endDateObj = new Date(end);
  const headers = [];

  while (startDateObj <= endDateObj) {
    headers.push(startDateObj.getDate());
    startDateObj.setDate(startDateObj.getDate() + 1);
  }

  return headers;
};

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

const getMonthStartDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split("T")[0];
};

const getMonthEndDate = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .split("T")[0];
};

const AttendanceView = () => {
  const [selectedState, setSelectedState] = useState("");

  // MODIFIED: Initialize empty strings so calendar inputs show empty
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState({});
  const [states, setStates] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  // ADDED: Helper functions to fallback to month start/end if empty
  const getEffectiveStartDate = () => {
    return startDate || getMonthStartDate();
  };

  const getEffectiveEndDate = () => {
    return endDate || getMonthEndDate();
  };

  // MODIFIED: Use effective dates to generate date headers
  const dateHeaders = generateDateHeadersInRange(
    getEffectiveStartDate(),
    getEffectiveEndDate()
  );

  const summaryHeaders = [
    "Weekly W",
    "Holidays",
    "Presence",
    "Leave",
    "Extra Working",
    "Paid Days",
    "Monthly Hours",
    "Present Days",
  ];

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      // MODIFIED: Use effective dates here to fetch data for full range if empty
      const params = {
        start_date: getEffectiveStartDate(),
        end_date: getEffectiveEndDate(),
        state: selectedState || undefined,
      };

      const response = await api.get("/employee/attendance/employee-details", {
        params,
      });
      const { data } = response.data;

      const selectedMonth = getEffectiveStartDate().slice(0, 7);

      const stateWiseData = {};

      data.employees.forEach((emp) => {
        const state = emp.state || "Unknown";
        if (!stateWiseData[state]) stateWiseData[state] = [];

        const start = new Date(getEffectiveStartDate());
        const end = new Date(getEffectiveEndDate());
        const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

        const attendanceArray = Array(diffDays).fill({
          status: "",
          hours: null,
        });

        Object.entries(emp.attendance).forEach(([dateStr, attendanceObj]) => {
          const dateObj = new Date(dateStr);
          if (dateObj >= start && dateObj <= end) {
            const dayIndex = Math.floor(
              (dateObj - start) / (1000 * 60 * 60 * 24)
            );
            const status = attendanceObj?.status || "";
            const daily_hours = attendanceObj?.daily_hours || 0;

            let shortStatus = "";
            if (status === "Present") shortStatus = "P";
            else if (status === "Absent") shortStatus = "A";
            else shortStatus = status.charAt(0);

            attendanceArray[dayIndex] = {
              status: shortStatus,
              hours: daily_hours,
            };
          }
        });

        stateWiseData[state].push({
          name: emp.name,
          designation: emp.designation || "-",
          attendance: attendanceArray,
          weeklyW: "-",
          holidays: "-",
          presence: "-",
          leave: "-",
          extraWorking: "-",
          paidDays: "-",
          monthlyHours: emp.monthly_hours?.[selectedMonth] || 0,
          presentDays: emp.monthly_present_days?.[selectedMonth] || 0,
        });
      });

      setEmployeeData(stateWiseData);
    } catch (err) {
      console.error("Failed to fetch data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [startDate, endDate, selectedState]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await api.get("/get/state");
        if (response.data && response.data.success) {
          setStates(response.data.data);
        } else {
          console.error("Failed to fetch states");
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      }
    };

    fetchStates();
  }, []);

  const [showDatePickers, setShowDatePickers] = useState(false);

  const downloadPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const title = `Attendance - RANGE - ${getEffectiveStartDate()} to ${getEffectiveEndDate()}`;
    doc.setFontSize(14);
    doc.text(title, 40, 40);

    const allMonthlyRows = [];

    Object.entries(employeeData)
      .filter(([state]) => !selectedState || state === selectedState)
      .forEach(([state, employees]) => {
        const filteredEmployees = employees.filter(
          (emp) =>
            emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.designation.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filteredEmployees.length === 0) return;

        allMonthlyRows.push([
          {
            content: `State: ${state}`,
            colSpan: 41,
            styles: {
              halign: "left",
              fillColor: [91, 140, 81],
              textColor: [255, 255, 255],
            },
          },
        ]);

        filteredEmployees.forEach((emp, index) => {
          const row = [
            index + 1,
            emp.name,
            emp.designation,
            ...emp.attendance.map((a) => a.status),
            emp.weeklyW,
            emp.holidays,
            emp.presence,
            emp.leave,
            emp.extraWorking,
            emp.paidDays,
            emp.monthlyHours,
            emp.presentDays,
          ];
          allMonthlyRows.push(row);
        });
      });

    const headRow = [
      "S.No",
      "Name",
      "Designation",
      ...dateHeaders.map(String),
      ...summaryHeaders,
    ];

    autoTable(doc, {
      startY: 60,
      head: [headRow],
      body: allMonthlyRows,
      theme: "striped",
      styles: {
        fontSize: 6,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
      },
    });

    doc.save(
      `attendance_range_${getEffectiveStartDate()}_to_${getEffectiveEndDate()}.pdf`
    );
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(employeeData)
      .filter(([state]) => !selectedState || state === selectedState)
      .forEach(([state, employees]) => {
        const filteredEmployees = employees.filter(
          (emp) =>
            emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.designation.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filteredEmployees.length === 0) return;

        const sheetData = [];

        const headers = [
          "S.No",
          "Name",
          "Designation",
          ...dateHeaders.map((d) => `Day ${d}`),
          ...summaryHeaders,
        ];
        sheetData.push(headers);

        filteredEmployees.forEach((emp, index) => {
          const row = [
            index + 1,
            emp.name,
            emp.designation,
            ...emp.attendance.map((a) => a.status),
            emp.weeklyW,
            emp.holidays,
            emp.presence,
            emp.leave,
            emp.extraWorking,
            emp.paidDays,
            emp.monthlyHours,
            emp.presentDays,
          ];
          sheetData.push(row);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(workbook, worksheet, state);
      });

    const fileName = `attendance_range_${getEffectiveStartDate()}_to_${getEffectiveEndDate()}.xlsx`;
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      fileName
    );
  };

  return (
    <section className="employee-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Attendance View</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div
              className="state-select tooltip-wrapper"
              data-tooltip="Filter by State"
            >
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All States</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_title}>
                    {state.state_title}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="date-filter tooltip-wrapper"
              style={{ position: "relative" }}
              data-tooltip="Filter by Date"
            >
              <button onClick={() => setShowDatePickers((prev) => !prev)}>
                <i className="fa-solid fa-calendar"></i>
              </button>
              {showDatePickers && (
                <div
                  className="date-popup"
                  style={{
                    position: "fixed",
                    top: "125px",
                    right: "15px",
                    background: "#fff",
                    padding: "12px 15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <input
                    ref={startDateInputRef}
                    type="date"
                    value={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setStartDate(val);
                      if (val && val > endDate) setEndDate(val);
                      if (val && endDate) setShowDatePickers(false);
                    }}
                  />
                  <input
                    ref={endDateInputRef}
                    type="date"
                    value={endDate}
                    min={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEndDate(val);
                      if (val && val < startDate) setStartDate(val);
                      if (startDate && val) setShowDatePickers(false);
                    }}
                  />
                </div>
              )}
            </div>

            <button className="download-btn" onClick={downloadPDF}>
              <i className="fa-regular fa-file-pdf"></i>&nbsp;Download
            </button>

            <button className="download-btn" onClick={downloadExcel}>
              <i className="fa-regular fa-file-excel"></i>&nbsp;Export Excel
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="attendance-container">
            <div className="table-wrapperr">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Designation</th>
                    {dateHeaders.map((date) => (
                      <th key={date}>{date}</th>
                    ))}
                    {summaryHeaders.map((label, i) => (
                      <th key={`s-${i}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(employeeData)
                    .filter(
                      ([state]) => !selectedState || state === selectedState
                    )
                    .map(([state, employees], i) => {
                      const filteredEmployees = employees.filter(
                        (emp) =>
                          emp.name
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()) ||
                          emp.designation
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                      );

                      if (filteredEmployees.length === 0) return null;

                      return (
                        <React.Fragment key={i}>
                          <tr className="state-row">
                            <td
                              className="state"
                              colSpan={
                                3 + dateHeaders.length + summaryHeaders.length
                              }
                            >
                              State: {state}
                            </td>
                          </tr>
                          {filteredEmployees.map((emp, empIndex) => (
                            <tr key={empIndex}>
                              <td>{empIndex + 1}</td>
                              <td>{emp.name}</td>
                              <td>{emp.designation}</td>
                              {emp.attendance.map((entry, dayIndex) => (
                                <td
                                  key={dayIndex}
                                  title={
                                    entry.hours != null
                                      ? `Hours: ${entry.hours}`
                                      : ""
                                  }
                                  style={{
                                    color:
                                      entry.status === "P"
                                        ? "#28a745"
                                        : entry.status === "A"
                                        ? "#f64b34"
                                        : "inherit",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {entry.status}
                                </td>
                              ))}

                              <td>{emp.weeklyW}</td>
                              <td>{emp.holidays}</td>
                              <td>{emp.presence}</td>
                              <td>{emp.leave}</td>
                              <td>{emp.extraWorking}</td>
                              <td>{emp.paidDays}</td>
                              <td className="check-in">{emp.monthlyHours}</td>
                              <td className="p-day">{emp.presentDays}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AttendanceView;
